import WinChancesIcon from "@mui/icons-material/Assessment";
import FirmIcon from "@mui/icons-material/Business";
import ComparisonIcon from "@mui/icons-material/CompareArrows";
import QuantumsIcon from "@mui/icons-material/CreditCard";
import SettledVsLitigatedIcon from "@mui/icons-material/Difference";
import SettlementsIcon from "@mui/icons-material/Handshake";
import NetResultIcon from "@mui/icons-material/Iso";
import ValueIcon from "@mui/icons-material/Money";
import RevenueIcon from "@mui/icons-material/Moving";
import ClaimsIcon from "@mui/icons-material/Forum";
import LargestIcon from "@mui/icons-material/FormatListNumbered";
import ClientsIcon from "@mui/icons-material/People";
import ReservesIcon from "@mui/icons-material/Savings";
import ClassesIcon from "@mui/icons-material/TableRows";
import VarIcon from "@mui/icons-material/ScatterPlot";
import MarginIcon from "@mui/icons-material/StackedBarChart";
import {
  Box,
  Drawer,
  ListItemButton,
  Toolbar,
  useMediaQuery,
} from "@mui/material";
import { alpha } from "@mui/material/styles";
import { ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { Link, NavLink, useLocation } from "react-router-dom";
import { usePortfolio } from "../../context/PortfolioContext";
import { useTheme } from "../../context/ThemeContext";
import { usePersona } from "../../context/usePersona";
import { Persona } from "../../shared/persona";
import { SectionLabel } from "../general/SectionLabel";
import { PortfolioCaseCount } from "./PortfolioCaseCount";

/** How wide the navigation is with its labels, and as a rail of icons alone. */
const WIDTH = 232;
const RAIL_WIDTH = 56;

/** How strongly the open page is washed with the primary colour. */
const OPEN_PAGE_ALPHA = 0.1;

/** The pages of the portfolio, in the order of the navigation. */
export const PORTFOLIO_PAGES = [
  "firm",
  "claims",
  "clients",
  "settlements",
] as const;
export type PortfolioPageKey = (typeof PORTFOLIO_PAGES)[number];

/** The pages of one role alone; every other page is every role's. */
const PERSONA_OF_PAGE: { [page in PortfolioPageKey]?: Persona } = {
  firm: "lawFirm",
  // what an insurer's claims cost
  claims: "insurer",
};

/** The pages of the portfolio a role has, in the order of the navigation. */
export const portfolioPagesOf = (persona: Persona): PortfolioPageKey[] =>
  PORTFOLIO_PAGES.filter(
    (page) => (PERSONA_OF_PAGE[page] ?? persona) === persona,
  );

/** The page of the portfolio that is open at a path, if it is one of them. */
export const portfolioPageAt = (
  pathname: string,
): PortfolioPageKey | undefined =>
  PORTFOLIO_PAGES.find((page) => pathname.startsWith(`/portfolio/${page}`));

/** A section of a page: the id of its heading, and its icon on the rail. */
type Section = { hash: string; icon: ReactNode };

const PAGE_ICONS: { [page in PortfolioPageKey]: ReactNode } = {
  firm: <FirmIcon />,
  claims: <ClaimsIcon />,
  clients: <ClientsIcon />,
  settlements: <SettlementsIcon />,
};

/**
 * The pages of the portfolio, beside them: how many cases the filters let
 * through, the three pages, and under the page that is open its sections to
 * jump to. Every link carries the filters on.
 */
export const PortfolioNavigation = () => {
  const { theme } = useTheme();
  const { t } = useTranslation(undefined, { keyPrefix: "portfolio" });
  const isRail = useMediaQuery(theme.breakpoints.down("md"));
  const width = isRail ? RAIL_WIDTH : WIDTH;
  const { pathname, search } = useLocation();
  const { totalAcceptedSettlements } = usePortfolio();
  const openPage = portfolioPageAt(pathname);
  const { active: persona } = usePersona();

  // the page of the settlements has no settlement filter, so its links drop it
  const settlementsSearchParams = new URLSearchParams(search);
  settlementsSearchParams.delete("settlement");

  const allPages: {
    key: PortfolioPageKey;
    search: string;
    sections: Section[];
  }[] = [
    {
      key: "firm",
      search,
      sections: [
        { hash: "netResult", icon: <NetResultIcon /> },
        { hash: "revenue", icon: <RevenueIcon /> },
        { hash: "margin", icon: <MarginIcon /> },
      ],
    },
    {
      key: "claims",
      search,
      sections: [
        { hash: "reserves", icon: <ReservesIcon /> },
        { hash: "classes", icon: <ClassesIcon /> },
        { hash: "range", icon: <VarIcon /> },
        { hash: "largest", icon: <LargestIcon /> },
      ],
    },
    {
      key: "clients",
      search,
      sections: [
        { hash: "caseValues", icon: <ValueIcon /> },
        { hash: "quantums", icon: <QuantumsIcon /> },
        { hash: "winChances", icon: <WinChancesIcon /> },
        { hash: "var", icon: <VarIcon /> },
      ],
    },
    {
      key: "settlements",
      search: settlementsSearchParams.toString(),
      sections: [
        { hash: "overview", icon: <SettlementsIcon /> },
        // with no settlements the page has only its overview, so the other
        // entries would have no section to land on
        ...(totalAcceptedSettlements === 0
          ? []
          : [
              { hash: "quality", icon: <NetResultIcon /> },
              {
                hash: "settledVsLitigated",
                icon: <SettledVsLitigatedIcon />,
              },
              { hash: "savings", icon: <ValueIcon /> },
              { hash: "comparison", icon: <ComparisonIcon /> },
            ]),
      ],
    },
  ];
  const pages = allPages.filter(({ key }) =>
    portfolioPagesOf(persona).includes(key),
  );

  return (
    <Drawer
      variant="permanent"
      slotProps={{ paper: { elevation: 0 } }}
      sx={{
        flexShrink: 0,
        width,
        "& .MuiDrawer-paper": { width },
      }}
    >
      {/* the navigation starts under the app bar, which lies over it */}
      <Toolbar />
      <Box
        component="nav"
        aria-label={t("navigation")}
        sx={{
          display: "flex",
          flexDirection: "column",
          gap: 0.25,
          px: 1.25,
          pt: 1.75,
          pb: 2.75,
        }}
      >
        {/* a rail has no room for the count; the filter bar states it too */}
        {!isRail && (
          <Box sx={{ px: 1.25, pt: 0.5, pb: 1.5 }}>
            <SectionLabel>
              {t("section.portfolio.title", { keyPrefix: "home" })}
            </SectionLabel>
            <Box sx={{ mt: 0.375 }}>
              <PortfolioCaseCount />
            </Box>
          </Box>
        )}

        {pages.map(({ key, search: searchOfPage, sections }) => {
          const page = `/portfolio/${key}`;
          const label = t(`section.${key}`);
          return (
            <div key={key}>
              <ListItemButton
                component={NavLink}
                to={{ pathname: page, search: searchOfPage }}
                title={label}
                sx={{
                  flexGrow: 0,
                  minHeight: 38,
                  gap: 1.375,
                  // a rail is too narrow for padding beside the icon it centres
                  px: isRail ? 0 : 1.25,
                  py: 0,
                  borderRadius: 1.75,
                  justifyContent: isRail ? "center" : "flex-start",
                  typography: "body2",
                  fontWeight: 500,
                  color: "text.primary",
                  "& .MuiSvgIcon-root": {
                    fontSize: 20,
                    color: "text.secondary",
                  },
                  // NavLink marks the link of the page that is open
                  "&.active": {
                    bgcolor: alpha(theme.palette.primary.main, OPEN_PAGE_ALPHA),
                    color: "primary.main",
                    fontWeight: 700,
                    "& .MuiSvgIcon-root": { color: "inherit" },
                  },
                }}
              >
                {PAGE_ICONS[key]}
                {!isRail && (
                  <Box component="span" sx={{ flex: 1, minWidth: 0 }}>
                    {label}
                  </Box>
                )}
              </ListItemButton>

              {/* the sections of the page that is open, to jump to */}
              {key === openPage && (
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    mt: 0.25,
                    mb: 0.75,
                    // on a rail the sections stand under the icon of their
                    // page; beside the labels a line ties them to it
                    ...(!isRail && {
                      ml: 2.25,
                      pl: 1.25,
                      borderLeft: 1,
                      borderColor: "divider",
                    }),
                  }}
                >
                  {sections.map(({ hash, icon }) => {
                    const sectionLabel = t(`subsection.${hash}`);
                    return (
                      <ListItemButton
                        key={hash}
                        component={Link}
                        to={{ pathname: page, search: searchOfPage, hash }}
                        title={sectionLabel}
                        aria-label={isRail ? sectionLabel : undefined}
                        sx={{
                          flexGrow: 0,
                          minHeight: 30,
                          px: isRail ? 0 : 1.25,
                          py: 0,
                          borderRadius: 1.5,
                          justifyContent: isRail ? "center" : "flex-start",
                          typography: "body2",
                          fontSize: 13,
                          fontWeight: 500,
                          color: "text.secondary",
                          "& .MuiSvgIcon-root": { fontSize: 18 },
                        }}
                      >
                        {isRail ? icon : sectionLabel}
                      </ListItemButton>
                    );
                  })}
                </Box>
              )}
            </div>
          );
        })}
      </Box>
    </Drawer>
  );
};
